import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
  Globe, Zap, Play, Square, Trash2, Pencil,
  ChevronDown, ChevronUp, RefreshCw,
  CheckCircle, XCircle, Clock, AlertTriangle, FileText,
} from 'lucide-react';

// ── helpers ────────────────────────────────────────────────────────────────────

const fmt = (dt) => dt ? new Date(dt).toLocaleString() : '—';

const durStr = (startedAt, completedAt) => {
  if (!startedAt) return '—';
  const end = completedAt ? new Date(completedAt) : new Date();
  const s = Math.round((end - new Date(startedAt)) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
};

const STATUS_STYLES = {
  running:    { bg: '#dbeafe', color: '#1d4ed8' },
  cancelling: { bg: '#fef3c7', color: '#b45309' },
  failed:     { bg: '#fee2e2', color: '#dc2626' },
  idle:       { bg: '#f1f5f9', color: '#475569' },
};

const StatusBadge = ({ job }) => {
  const s = job.status || 'idle';
  const styles = STATUS_STYLES[s] || STATUS_STYLES.idle;
  const icons = {
    running:    <RefreshCw size={11} className="spin" />,
    cancelling: <AlertTriangle size={11} />,
    failed:     <XCircle size={11} />,
    idle:       <CheckCircle size={11} />,
  };
  const labels = {
    running:    'Running',
    cancelling: 'Stopping…',
    failed:     'Failed',
    idle:       job.last_success_at ? 'Idle' : 'Never run',
  };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
      background: styles.bg, color: styles.color,
    }}>
      {icons[s]} {labels[s]}
    </span>
  );
};

const ExecStatusIcon = ({ status }) => {
  if (status === 'completed') return <CheckCircle size={13} style={{ color: '#10b981' }} />;
  if (status === 'failed')    return <XCircle    size={13} style={{ color: '#ef4444' }} />;
  if (status === 'running' || status === 'cancelling')
    return <RefreshCw size={13} className="spin" style={{ color: '#3b82f6' }} />;
  return <Clock size={13} style={{ color: '#94a3b8' }} />;
};

// ── form defaults ──────────────────────────────────────────────────────────────

const WEBSITE_DEFAULTS = {
  job_type: 'website', name: '', description: '',
  seed_urls: '', allowed_domains: '', exclude_patterns: '',
  max_pages: 500, max_depth: 5, request_delay_ms: 500,
  respect_robots: true, recrawl_policy: 'smart',
  full_recrawl_every_days: 30,
  follow_document_links: false, document_domains: '', min_content_length: 300,
  schedule_enabled: true, schedule_type: 'weekly',
};

const API_DEFAULTS = {
  job_type: 'api', name: '', description: '',
  url: '', http_method: 'GET', headers: '', request_body: '',
  replace_existing: true, data_instruction: '',
  schedule_enabled: true, schedule_type: 'daily',
};

// ── component ──────────────────────────────────────────────────────────────────

const DataSources = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenants, setTenants]           = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(searchParams.get('tenant_id') || '');
  const [jobs, setJobs]                 = useState([]);
  const [loading, setLoading]           = useState(false);
  const [expanded, setExpanded]         = useState({});      // job_id → bool
  const [executions, setExecutions]     = useState({});      // job_id → []
  const [showForm, setShowForm]         = useState(false);
  const [formType, setFormType]         = useState('website');
  const [form, setForm]                 = useState(WEBSITE_DEFAULTS);
  const [saving, setSaving]             = useState(false);
  const [stopping, setStopping]         = useState({});      // job_id → bool
  const [deleteModal, setDeleteModal]   = useState(null);    // { jobId, name } | null
  const [editJob, setEditJob]           = useState(null);    // job object being edited | null
  const [editForm, setEditForm]         = useState({});
  const [editSaving, setEditSaving]     = useState(false);
  const pollRef = useRef(null);

  // ── tenant loading (same pattern as DocumentManager) ─────────────────────────

  useEffect(() => {
    axios.get('/api/tenant/').then(r => {
      const list = r.data || [];
      setTenants(list);
      if (!list.length) return;
      const fromUrl = searchParams.get('tenant_id');
      const fromStorage = localStorage.getItem('admin_selected_tenant_id');
      const ids = list.map(t => t.id);
      const pick = [fromUrl, fromStorage].find(id => id && ids.includes(id)) || list[0].id;
      setSelectedTenant(pick);
      setSearchParams({ tenant_id: pick }, { replace: true });
    }).catch(() => toast.error('Failed to load tenants'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const changeTenant = (id) => {
    setSelectedTenant(id);
    setSearchParams({ tenant_id: id }, { replace: true });
    if (id) localStorage.setItem('admin_selected_tenant_id', id);
    setJobs([]);
    setExpanded({});
    setExecutions({});
  };

  // ── job loading + polling ─────────────────────────────────────────────────────

  const loadJobs = useCallback(async (silent = false) => {
    if (!selectedTenant) return;
    if (!silent) setLoading(true);
    try {
      const r = await axios.get('/api/crawler-jobs/', { params: { tenant_id: selectedTenant } });
      setJobs(r.data);
    } catch {
      if (!silent) toast.error('Failed to load data sources');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedTenant]);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // Poll every 4s while any job is running
  useEffect(() => {
    const anyRunning = jobs.some(j => j.status === 'running' || j.status === 'cancelling');
    if (anyRunning) {
      pollRef.current = setInterval(() => loadJobs(true), 4000);
    }
    return () => clearInterval(pollRef.current);
  }, [jobs, loadJobs]);

  // Refresh open execution panels when jobs refresh
  useEffect(() => {
    const openIds = Object.keys(expanded).filter(id => expanded[id]);
    openIds.forEach(id => refreshExecutions(Number(id)));
  }, [jobs]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── actions ───────────────────────────────────────────────────────────────────

  const triggerJob = async (jobId) => {
    try {
      await axios.post(`/api/crawler-jobs/${jobId}/run`);
      toast.success('Crawl started');
      await loadJobs(true);
    } catch {
      toast.error('Failed to start crawl');
    }
  };

  const stopJob = async (jobId) => {
    setStopping(s => ({ ...s, [jobId]: true }));
    try {
      await axios.post(`/api/crawler-jobs/${jobId}/stop`);
      toast.success('Stop requested — crawl will finish the current page then exit');
      await loadJobs(true);
    } catch {
      toast.error('Failed to stop crawl');
    } finally {
      setStopping(s => ({ ...s, [jobId]: false }));
    }
  };

  const confirmDelete = async (withDocs) => {
    if (!deleteModal) return;
    const { jobId } = deleteModal;
    setDeleteModal(null);
    try {
      await axios.delete(`/api/crawler-jobs/${jobId}`, { params: { delete_documents: withDocs } });
      toast.success(withDocs ? 'Job and all crawled documents removed' : 'Data source deactivated — documents kept');
      loadJobs();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const toggleExecutions = async (jobId) => {
    const next = !expanded[jobId];
    setExpanded(e => ({ ...e, [jobId]: next }));
    if (next) await refreshExecutions(jobId);
  };

  const refreshExecutions = async (jobId) => {
    try {
      const r = await axios.get(`/api/crawler-jobs/${jobId}/executions`);
      setExecutions(e => ({ ...e, [jobId]: r.data }));
    } catch { /* silent */ }
  };

  // ── edit helpers ──────────────────────────────────────────────────────────────

  const openEdit = (job) => {
    setEditJob(job);
    setEditForm({
      name:        job.name,
      description: job.description || '',
      // website
      seed_urls:        (job.seed_urls || []).join('\n'),
      allowed_domains:  (job.allowed_domains || []).join('\n'),
      exclude_patterns: (job.exclude_patterns || []).join('\n'),
      max_pages:        job.max_pages ?? 500,
      max_depth:        job.max_depth ?? 5,
      request_delay_ms: job.request_delay_ms ?? 500,
      respect_robots:   job.respect_robots ?? true,
      recrawl_policy:   job.recrawl_policy || 'smart',
      full_recrawl_every_days: job.full_recrawl_every_days ?? 30,
      follow_document_links: job.follow_document_links ?? false,
      document_domains: (job.document_domains || []).join('\n'),
      min_content_length: job.min_content_length ?? 300,
      // api
      url:             job.url || '',
      http_method:     job.http_method || 'GET',
      headers:         job.headers ? JSON.stringify(job.headers, null, 2) : '',
      request_body:    job.request_body ? JSON.stringify(job.request_body, null, 2) : '',
      data_instruction: job.data_instruction || '',
      replace_existing: job.replace_existing ?? true,
      // schedule
      schedule_enabled: job.schedule_enabled ?? true,
      schedule_type:    job.schedule_type || 'daily',
    });
  };

  const setEditField = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked
              : e.target.type === 'number'   ? Number(e.target.value)
              : e.target.value;
    setEditForm(f => ({ ...f, [field]: val }));
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editJob) return;
    setEditSaving(true);
    try {
      const payload = { ...editForm };
      if (editJob.job_type === 'website') {
        payload.seed_urls        = editForm.seed_urls.split('\n').map(s => s.trim()).filter(Boolean);
        payload.allowed_domains  = editForm.allowed_domains  ? editForm.allowed_domains.split('\n').map(s => s.trim()).filter(Boolean)  : null;
        payload.exclude_patterns = editForm.exclude_patterns ? editForm.exclude_patterns.split('\n').map(s => s.trim()).filter(Boolean) : null;
        payload.document_domains = editForm.document_domains ? editForm.document_domains.split('\n').map(s => s.trim()).filter(Boolean) : null;
      } else {
        try { payload.headers      = editForm.headers      ? JSON.parse(editForm.headers)      : null; }
        catch { toast.error('Headers must be valid JSON'); setEditSaving(false); return; }
        try { payload.request_body = editForm.request_body ? JSON.parse(editForm.request_body) : null; }
        catch { toast.error('Request body must be valid JSON'); setEditSaving(false); return; }
      }
      await axios.patch(`/api/crawler-jobs/${editJob.id}`, payload);
      toast.success('Data source updated');
      setEditJob(null);
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update data source');
    } finally {
      setEditSaving(false);
    }
  };

  // ── create form helpers ───────────────────────────────────────────────────────

  const openForm = (type) => {
    setFormType(type);
    setForm(type === 'website' ? { ...WEBSITE_DEFAULTS } : { ...API_DEFAULTS });
    setShowForm(true);
  };

  const setField = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked
              : e.target.type === 'number'   ? Number(e.target.value)
              : e.target.value;
    setForm(f => ({ ...f, [field]: val }));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, tenant_id: selectedTenant };
      if (form.job_type === 'website') {
        payload.seed_urls = form.seed_urls.split('\n').map(s => s.trim()).filter(Boolean);
        payload.allowed_domains = form.allowed_domains
          ? form.allowed_domains.split('\n').map(s => s.trim()).filter(Boolean) : null;
        payload.exclude_patterns = form.exclude_patterns
          ? form.exclude_patterns.split('\n').map(s => s.trim()).filter(Boolean) : null;
        payload.document_domains = form.document_domains
          ? form.document_domains.split('\n').map(s => s.trim()).filter(Boolean) : null;
      } else {
        try { payload.headers = form.headers ? JSON.parse(form.headers) : null; }
        catch { toast.error('Headers must be valid JSON'); setSaving(false); return; }
        try { payload.request_body = form.request_body ? JSON.parse(form.request_body) : null; }
        catch { toast.error('Request body must be valid JSON'); setSaving(false); return; }
      }
      await axios.post('/api/crawler-jobs/', payload);
      toast.success('Data source created');
      setShowForm(false);
      loadJobs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create data source');
    } finally {
      setSaving(false);
    }
  };

  // ── render ────────────────────────────────────────────────────────────────────

  return (
    <div className="document-manager">

      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">Data Sources</h1>
          <p className="page-subtitle">
            Crawl websites or poll APIs — results appear in{' '}
            <Link to={`/documents?tenant_id=${selectedTenant}`} style={{ color: '#667eea' }}>
              Document Manager
            </Link>
          </p>
        </div>
        <div className="header-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            className="form-select tenant-selector"
            value={selectedTenant}
            onChange={e => changeTenant(e.target.value)}
          >
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {selectedTenant && (
            <>
              <button className="btn btn-secondary" onClick={() => openForm('website')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Globe size={15} /> Website crawler
              </button>
              <button className="btn btn-secondary" onClick={() => openForm('api')}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Zap size={15} /> API source
              </button>
            </>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && <div className="empty-state"><p>Loading…</p></div>}

      {/* Job list */}
      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 24 }}>
          {jobs.length === 0 && (
            <div className="empty-state">
              <Globe size={48} style={{ color: '#cbd5e1' }} />
              <p>No data sources yet. Add a website or API source above.</p>
            </div>
          )}

          {jobs.map(job => (
            <div key={job.id} className="document-card" style={{ padding: 20 }}>
              {/* Job header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                    {job.job_type === 'website'
                      ? <Globe size={16} style={{ color: '#667eea', flexShrink: 0 }} />
                      : <Zap   size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />}
                    <span style={{ fontWeight: 700, fontSize: '1rem' }}>{job.name}</span>
                    <span style={{
                      fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                      background: job.job_type === 'website' ? '#ede9fe' : '#fef3c7',
                      color: job.job_type === 'website' ? '#7c3aed' : '#b45309',
                    }}>
                      {job.job_type === 'website' ? 'Website' : 'API'}
                    </span>
                    <StatusBadge job={job} />
                  </div>
                  {job.description && (
                    <p style={{ margin: '0 0 6px', fontSize: '0.85rem', color: '#64748b' }}>{job.description}</p>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 20px', fontSize: '0.8rem', color: '#64748b' }}>
                    {job.job_type === 'website' && job.seed_urls?.length > 0 && (
                      <span>Seeds: {job.seed_urls.slice(0, 2).join(', ')}{job.seed_urls.length > 2 ? ` +${job.seed_urls.length - 2}` : ''}</span>
                    )}
                    {job.job_type === 'api' && <span>URL: {job.url}</span>}
                    <span>Schedule: {job.schedule_type}</span>
                    <span>Last run: {fmt(job.last_run_at)}</span>
                    {job.total_runs > 0 && <span>Runs: {job.success_runs}/{job.total_runs} ok</span>}
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                  {/* Docs shortcut */}
                  <Link
                    to={`/documents?tenant_id=${selectedTenant}`}
                    className="btn btn-secondary"
                    style={{ padding: '6px 10px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}
                    title="View indexed documents"
                  >
                    <FileText size={13} /> Docs
                  </Link>

                  {/* Start / Retry — when not running */}
                  {job.status === 'idle' && (
                    <button className="btn btn-primary"
                      style={{ padding: '6px 14px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}
                      onClick={() => triggerJob(job.id)}>
                      <Play size={13} /> Start
                    </button>
                  )}
                  {job.status === 'failed' && (
                    <button className="btn btn-primary"
                      style={{ padding: '6px 14px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}
                      onClick={() => triggerJob(job.id)}>
                      <Play size={13} /> Retry
                    </button>
                  )}

                  {/* Stop — when running */}
                  {job.status === 'running' && (
                    <button className="btn btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5, color: '#dc2626', borderColor: '#fca5a5' }}
                      onClick={() => stopJob(job.id)}
                      disabled={stopping[job.id]}>
                      <Square size={13} /> {stopping[job.id] ? 'Stopping…' : 'Stop'}
                    </button>
                  )}

                  {/* Stopping — disabled while waiting */}
                  {job.status === 'cancelling' && (
                    <button className="btn btn-secondary" disabled
                      style={{ padding: '6px 12px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5, color: '#f59e0b' }}>
                      <RefreshCw size={13} className="spin" /> Stopping…
                    </button>
                  )}

                  {/* History toggle */}
                  <button className="btn btn-secondary"
                    style={{ padding: '6px 10px', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: 5 }}
                    onClick={() => toggleExecutions(job.id)}>
                    {expanded[job.id] ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    History
                  </button>

                  {/* Edit */}
                  <button className="btn btn-secondary"
                    style={{ padding: '6px 10px', fontSize: '0.82rem' }}
                    onClick={() => openEdit(job)}
                    disabled={job.status === 'running' || job.status === 'cancelling'}
                    title="Edit configuration">
                    <Pencil size={13} />
                  </button>

                  {/* Delete — disabled while running */}
                  <button className="btn btn-secondary"
                    style={{ padding: '6px 10px', fontSize: '0.82rem', color: '#dc2626' }}
                    onClick={() => setDeleteModal({ jobId: job.id, name: job.name })}
                    disabled={job.status === 'running' || job.status === 'cancelling'}
                    title={job.status === 'running' ? 'Stop the crawl before deleting' : 'Remove'}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>

              {/* Running progress strip */}
              {job.status === 'running' && (
                <div style={{ marginTop: 12, height: 3, borderRadius: 2, background: '#e0e7ff', overflow: 'hidden' }}>
                  <div style={{ height: '100%', background: '#667eea', animation: 'progress-pulse 2s ease-in-out infinite', width: '60%' }} />
                </div>
              )}

              {/* Execution history panel */}
              {expanded[job.id] && (
                <div style={{ marginTop: 16, borderTop: '1px solid #e2e8f0', paddingTop: 14 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Recent runs</span>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '2px 8px', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}
                      onClick={() => refreshExecutions(job.id)}
                    >
                      <RefreshCw size={11} /> Refresh
                    </button>
                  </div>

                  {!executions[job.id] && (
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>Loading…</p>
                  )}
                  {executions[job.id]?.length === 0 && (
                    <p style={{ color: '#94a3b8', fontSize: '0.85rem' }}>No runs yet.</p>
                  )}
                  {executions[job.id]?.length > 0 && (
                    <table style={{ width: '100%', fontSize: '0.8rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ color: '#94a3b8', textAlign: 'left' }}>
                          <th style={{ padding: '3px 8px', fontWeight: 600 }}>Started</th>
                          <th style={{ padding: '3px 8px', fontWeight: 600 }}>Duration</th>
                          <th style={{ padding: '3px 8px', fontWeight: 600 }}>Status</th>
                          <th style={{ padding: '3px 8px', fontWeight: 600 }}>Indexed</th>
                          <th style={{ padding: '3px 8px', fontWeight: 600 }}>Removed</th>
                          <th style={{ padding: '3px 8px', fontWeight: 600 }}>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {executions[job.id].map(ex => (
                          <tr key={ex.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '6px 8px' }}>{fmt(ex.started_at)}</td>
                            <td style={{ padding: '6px 8px' }}>{durStr(ex.started_at, ex.completed_at)}</td>
                            <td style={{ padding: '6px 8px' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <ExecStatusIcon status={ex.status} />
                                {ex.status}
                              </span>
                            </td>
                            <td style={{ padding: '6px 8px' }}>{ex.records_created ?? '—'}</td>
                            <td style={{ padding: '6px 8px' }}>{ex.records_deleted ?? '—'}</td>
                            <td style={{
                              padding: '6px 8px', color: '#dc2626',
                              maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }} title={ex.error_message || ''}>
                              {ex.error_message || ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create form modal */}
      {showForm && (
        <div className="modal" onClick={() => setShowForm(false)}>
          <div className="modal-content" style={{ maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                {formType === 'website' ? <><Globe size={18} /> New website crawler</> : <><Zap size={18} /> New API source</>}
              </h2>
              <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            </div>

            <form onSubmit={submitForm} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-control" value={form.name} onChange={setField('name')} required
                  placeholder={formType === 'website' ? 'e.g. Company Website' : 'e.g. Products API'} />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="form-control" value={form.description} onChange={setField('description')}
                  placeholder="Optional note" />
              </div>

              {/* ── Website fields ── */}
              {formType === 'website' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Seed URLs * <span style={{ fontWeight: 400, color: '#64748b' }}>(one per line)</span></label>
                    <textarea className="form-control" rows={3} value={form.seed_urls} onChange={setField('seed_urls')} required
                      placeholder={'https://example.com\nhttps://example.com/blog'} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Allowed domains <span style={{ fontWeight: 400, color: '#64748b' }}>(blank = seed domains only)</span></label>
                    <textarea className="form-control" rows={2} value={form.allowed_domains} onChange={setField('allowed_domains')}
                      placeholder={'example.com\ndocs.example.com'} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Exclude URL patterns <span style={{ fontWeight: 400, color: '#64748b' }}>(regex, one per line)</span></label>
                    <textarea className="form-control" rows={2} value={form.exclude_patterns} onChange={setField('exclude_patterns')}
                      placeholder={'/wp-admin/\n/cart\n\\?s='} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label className="form-label">Max pages</label>
                      <input className="form-control" type="number" min={1} max={10000} value={form.max_pages} onChange={setField('max_pages')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Max depth</label>
                      <input className="form-control" type="number" min={1} max={20} value={form.max_depth} onChange={setField('max_depth')} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Delay (ms)</label>
                      <input className="form-control" type="number" min={0} max={10000} value={form.request_delay_ms} onChange={setField('request_delay_ms')} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label className="form-label">Recrawl policy</label>
                      <select className="form-select" value={form.recrawl_policy} onChange={setField('recrawl_policy')}>
                        <option value="smart">Smart (skip unchanged)</option>
                        <option value="full">Full (always re-index)</option>
                        <option value="none">Once (never revisit)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Full recrawl every (days)</label>
                      <input className="form-control" type="number" min={1} value={form.full_recrawl_every_days} onChange={setField('full_recrawl_every_days')} />
                    </div>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input type="checkbox" checked={form.respect_robots} onChange={setField('respect_robots')} />
                    Respect robots.txt
                  </label>

                  <div className="form-group">
                    <label className="form-label">Min content length (chars)</label>
                    <input className="form-control" type="number" min={0} value={form.min_content_length} onChange={setField('min_content_length')}
                      placeholder="300" />
                    <small style={{ color: '#64748b' }}>Pages with less extracted text are skipped (avoids cookie-banner-only pages)</small>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input type="checkbox" checked={form.follow_document_links} onChange={setField('follow_document_links')} />
                    Follow &amp; index document links (PDF, DOCX, XLSX…)
                  </label>

                  {form.follow_document_links && (
                    <div className="form-group">
                      <label className="form-label">Extra domains for documents <span style={{ fontWeight: 400, color: '#64748b' }}>(one per line)</span></label>
                      <textarea className="form-control" rows={2} value={form.document_domains} onChange={setField('document_domains')}
                        placeholder={"drive.google.com\nstorage.example.com"} />
                      <small style={{ color: '#64748b' }}>Domains outside the main crawl domain from which documents may be downloaded</small>
                    </div>
                  )}
                </>
              )}

              {/* ── API fields ── */}
              {formType === 'api' && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
                    <div className="form-group">
                      <label className="form-label">Method</label>
                      <select className="form-select" value={form.http_method} onChange={setField('http_method')}>
                        {['GET','POST','PUT','PATCH','DELETE'].map(m => <option key={m}>{m}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">URL *</label>
                      <input className="form-control" value={form.url} onChange={setField('url')} required
                        placeholder="https://api.example.com/data" />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Headers <span style={{ fontWeight: 400, color: '#64748b' }}>(JSON)</span></label>
                    <textarea className="form-control" rows={2} value={form.headers} onChange={setField('headers')}
                      placeholder={'{"Authorization": "Bearer TOKEN"}'}
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Request body <span style={{ fontWeight: 400, color: '#64748b' }}>(JSON, POST/PUT only)</span></label>
                    <textarea className="form-control" rows={2} value={form.request_body} onChange={setField('request_body')}
                      placeholder='{"key": "value"}'
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Data instruction</label>
                    <textarea className="form-control" rows={2} value={form.data_instruction} onChange={setField('data_instruction')}
                      placeholder="Describe what this data represents so the chatbot uses it correctly" />
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
                    <input type="checkbox" checked={form.replace_existing} onChange={setField('replace_existing')} />
                    Replace previous data on each run
                  </label>
                </>
              )}

              {/* ── Schedule ── */}
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', marginBottom: 12 }}>
                  <input type="checkbox" checked={form.schedule_enabled} onChange={setField('schedule_enabled')} />
                  Enable scheduled runs
                </label>
                {form.schedule_enabled && (
                  <div className="form-group">
                    <label className="form-label">Frequency</label>
                    <select className="form-select" value={form.schedule_type} onChange={setField('schedule_type')}>
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating…' : 'Create data source'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editJob && (
        <div className="modal" onClick={() => setEditJob(null)}>
          <div className="modal-content" style={{ maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                {editJob.job_type === 'website' ? <Globe size={18} /> : <Zap size={18} />}
                Edit "{editJob.name}"
              </h2>
              <button className="modal-close" onClick={() => setEditJob(null)}>×</button>
            </div>

            <form onSubmit={submitEdit} style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-control" value={editForm.name} onChange={setEditField('name')} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="form-control" value={editForm.description} onChange={setEditField('description')} />
              </div>

              {/* Website fields */}
              {editJob.job_type === 'website' && (<>
                <div className="form-group">
                  <label className="form-label">Seed URLs * <span style={{ fontWeight: 400, color: '#64748b' }}>(one per line)</span></label>
                  <textarea className="form-control" rows={3} value={editForm.seed_urls} onChange={setEditField('seed_urls')} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Allowed domains <span style={{ fontWeight: 400, color: '#64748b' }}>(blank = seed domains)</span></label>
                  <textarea className="form-control" rows={2} value={editForm.allowed_domains} onChange={setEditField('allowed_domains')} />
                </div>
                <div className="form-group">
                  <label className="form-label">Exclude URL patterns <span style={{ fontWeight: 400, color: '#64748b' }}>(regex, one per line)</span></label>
                  <textarea className="form-control" rows={2} value={editForm.exclude_patterns} onChange={setEditField('exclude_patterns')} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Max pages</label>
                    <input className="form-control" type="number" min={1} max={10000} value={editForm.max_pages} onChange={setEditField('max_pages')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Max depth</label>
                    <input className="form-control" type="number" min={1} max={20} value={editForm.max_depth} onChange={setEditField('max_depth')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Delay (ms)</label>
                    <input className="form-control" type="number" min={0} max={10000} value={editForm.request_delay_ms} onChange={setEditField('request_delay_ms')} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Recrawl policy</label>
                    <select className="form-select" value={editForm.recrawl_policy} onChange={setEditField('recrawl_policy')}>
                      <option value="smart">Smart (skip unchanged)</option>
                      <option value="full">Full (always re-index)</option>
                      <option value="none">Once (never revisit)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Full recrawl every (days)</label>
                    <input className="form-control" type="number" min={1} value={editForm.full_recrawl_every_days} onChange={setEditField('full_recrawl_every_days')} />
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input type="checkbox" checked={editForm.respect_robots} onChange={setEditField('respect_robots')} />
                  Respect robots.txt
                </label>

                <div className="form-group" style={{ marginTop: 8 }}>
                  <label className="form-label">Min content length (chars)</label>
                  <input className="form-control" type="number" min={0} value={editForm.min_content_length} onChange={setEditField('min_content_length')} />
                  <small style={{ color: '#64748b' }}>Pages with less extracted text are skipped</small>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', marginTop: 8 }}>
                  <input type="checkbox" checked={editForm.follow_document_links} onChange={setEditField('follow_document_links')} />
                  Follow &amp; index document links (PDF, DOCX, XLSX…)
                </label>

                {editForm.follow_document_links && (
                  <div className="form-group" style={{ marginTop: 8 }}>
                    <label className="form-label">Extra domains for documents <span style={{ fontWeight: 400, color: '#64748b' }}>(one per line)</span></label>
                    <textarea className="form-control" rows={2} value={editForm.document_domains} onChange={setEditField('document_domains')}
                      placeholder={"drive.google.com\nstorage.example.com"} />
                  </div>
                )}
              </>)}

              {/* API fields */}
              {editJob.job_type === 'api' && (<>
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
                  <div className="form-group">
                    <label className="form-label">Method</label>
                    <select className="form-select" value={editForm.http_method} onChange={setEditField('http_method')}>
                      {['GET','POST','PUT','PATCH','DELETE'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">URL *</label>
                    <input className="form-control" value={editForm.url} onChange={setEditField('url')} required />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Headers <span style={{ fontWeight: 400, color: '#64748b' }}>(JSON)</span></label>
                  <textarea className="form-control" rows={2} value={editForm.headers} onChange={setEditField('headers')}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Request body <span style={{ fontWeight: 400, color: '#64748b' }}>(JSON)</span></label>
                  <textarea className="form-control" rows={2} value={editForm.request_body} onChange={setEditField('request_body')}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">Data instruction</label>
                  <textarea className="form-control" rows={2} value={editForm.data_instruction} onChange={setEditField('data_instruction')} />
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem' }}>
                  <input type="checkbox" checked={editForm.replace_existing} onChange={setEditField('replace_existing')} />
                  Replace previous data on each run
                </label>
              </>)}

              {/* Schedule */}
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.9rem', marginBottom: 12 }}>
                  <input type="checkbox" checked={editForm.schedule_enabled} onChange={setEditField('schedule_enabled')} />
                  Enable scheduled runs
                </label>
                {editForm.schedule_enabled && (
                  <div className="form-group">
                    <label className="form-label">Frequency</label>
                    <select className="form-select" value={editForm.schedule_type} onChange={setEditField('schedule_type')}>
                      <option value="hourly">Hourly</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setEditJob(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={editSaving}>
                  {editSaving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteModal && (
        <div className="modal" onClick={() => setDeleteModal(null)}>
          <div className="modal-content" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Trash2 size={17} style={{ color: '#dc2626' }} /> Remove "{deleteModal.name}"
              </h2>
              <button className="modal-close" onClick={() => setDeleteModal(null)}>×</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#374151' }}>
                What would you like to do with the documents that were crawled by this job?
              </p>
              <button
                className="btn btn-secondary"
                style={{ textAlign: 'left', padding: '12px 16px' }}
                onClick={() => confirmDelete(false)}
              >
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Keep documents</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Deactivate the job only — crawled pages stay in the knowledge base.
                </div>
              </button>
              <button
                className="btn btn-secondary"
                style={{ textAlign: 'left', padding: '12px 16px', borderColor: '#fca5a5' }}
                onClick={() => confirmDelete(true)}
              >
                <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: 2 }}>Remove job + all documents</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Permanently delete the job and every document + chunk it crawled.
                </div>
              </button>
              <button className="btn btn-secondary" style={{ marginTop: 4 }} onClick={() => setDeleteModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes progress-pulse {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(60%); }
          100% { transform: translateX(200%); }
        }
      `}</style>
    </div>
  );
};

export default DataSources;
